import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, COMPANY } from "@/lib/config";
import useReveal from "@/hooks/useReveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const SERVICES = [
    "Diagnostic des pathologies",
    "Réparations / restauration",
    "Accompagnement de travaux",
    "Formations",
    "Autre",
];

export default function Contact() {
    const ref = useReveal();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        service: "",
        message: "",
    });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error("Merci de remplir les champs obligatoires");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message envoyé. Je vous réponds rapidement.");
            setForm({ name: "", email: "", phone: "", subject: "", service: "", message: "" });
        } catch (err) {
            toast.error("Erreur lors de l'envoi. Réessayez.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" ref={ref} data-testid="contact-section" className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-5 reveal">
                    <span className="label-overline text-brand">Contact</span>
                    <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                        Parlons de votre <span className="italic">maison.</span>
                    </h2>
                    <p className="mt-5 text-stone2 leading-relaxed text-base md:text-lg">
                        Décrivez votre projet ou vos désordres : je reviens vers vous
                        sous 48h ouvrées pour évaluer la pertinence d'une visite.
                    </p>

                    <div className="mt-10 space-y-6">
                        <ContactLine
                            icon={Phone}
                            label="Téléphone"
                            value={COMPANY.phone}
                            href={`tel:${COMPANY.phoneRaw}`}
                            testid="contact-link-phone"
                        />
                        <ContactLine
                            icon={Mail}
                            label="Email"
                            value={COMPANY.email}
                            href={`mailto:${COMPANY.email}`}
                            testid="contact-link-email"
                        />
                        <ContactLine
                            icon={MapPin}
                            label="Atelier"
                            value={COMPANY.address}
                            sub={`Zone : ${COMPANY.zone}`}
                            testid="contact-link-address"
                        />
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="lg:col-span-7 reveal bg-card border border-border rounded-sm p-6 md:p-10 space-y-5"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Nom & prénom" required>
                            <Input
                                data-testid="contact-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Jean Dupont"
                                required
                            />
                        </Field>
                        <Field label="Email" required>
                            <Input
                                data-testid="contact-input-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="vous@email.fr"
                                required
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Téléphone">
                            <Input
                                data-testid="contact-input-phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="06 12 34 56 78"
                            />
                        </Field>
                        <Field label="Service concerné">
                            <Select
                                value={form.service}
                                onValueChange={(v) => setForm({ ...form, service: v })}
                            >
                                <SelectTrigger data-testid="contact-service">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SERVICES.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <Field label="Sujet" required>
                        <Input
                            data-testid="contact-subject"
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            placeholder="Diagnostic humidité, devis enduit chaux…"
                            required
                        />
                    </Field>
                    <Field label="Votre message" required>
                        <Textarea
                            data-testid="contact-message"
                            rows={6}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Décrivez votre projet, votre maison (époque, matériaux), les désordres observés…"
                            required
                        />
                    </Field>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="group inline-flex items-center gap-2 px-7 py-3.5 bg-brand hover:bg-brand-hover disabled:opacity-60 text-cream rounded-sm font-medium tracking-tight transition-colors"
                    >
                        {submitting ? "Envoi…" : "Envoyer le message"}
                        <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </button>
                </form>
            </div>
        </section>
    );
}

function ContactLine({ icon: Icon, label, value, sub, href, testid }) {
    const inner = (
        <>
            <div className="w-11 h-11 rounded-sm bg-sand/60 border border-border flex items-center justify-center text-brand shrink-0">
                <Icon size={18} strokeWidth={1.6} />
            </div>
            <div>
                <div className="label-overline text-stone2">{label}</div>
                <div className="font-heading text-lg text-ink tracking-tight mt-0.5">{value}</div>
                {sub && <div className="text-xs text-stone2 mt-1">{sub}</div>}
            </div>
        </>
    );
    return href ? (
        <a href={href} data-testid={testid} className="flex gap-4 items-start group hover:text-brand">
            {inner}
        </a>
    ) : (
        <div className="flex gap-4 items-start" data-testid={testid}>
            {inner}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-ink/80 uppercase tracking-wider">
                {label} {required && <span className="text-brand">*</span>}
            </Label>
            {children}
        </div>
    );
}
